/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import useAsync from 'react-use/lib/useAsync';
import { Entity, parseEntityRef } from '@backstage/catalog-model';
import {
  discoveryApiRef,
  fetchApiRef,
  storageApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

// The "View as" lens, promoted from the Home page to an app-wide control. It's
// a CLIENT-SIDE scoping lens (it swaps which catalog refs a surface scopes by),
// not credential impersonation -- the signed-in user's token still backs every
// request, and the backend re-gates `?as=` on the m42.viewAs allow-list. Stock
// Backstage plugins (Catalog, My Tasks, Search) keep showing the real user;
// our m42 surfaces opt in by reading this context.

const STORAGE_BUCKET = 'viewAs';
const STORAGE_KEY = 'ref';

// Synthetic "read-only user" identity: a valid-but-nonexistent ref, so it
// resolves to no entity, no groups, and no owned components -- exactly what a
// signed-in-but-unmatched user (the read-only fallback) sees. Never sent as a
// `?as=` login (it isn't a real GitHub user).
export const READ_ONLY_REF = 'user:default/__read-only__';

const displayNameOf = (entity: Entity | undefined): string | undefined =>
  (entity?.spec?.profile as { displayName?: string } | undefined)
    ?.displayName ??
  entity?.metadata.title ??
  entity?.metadata.name;

export type ViewAsState = {
  // True when the signed-in user may use the lens (backend m42.viewAs gate).
  canViewAs: boolean;
  // The viewed user's entityRef (`user:default/<login>`), READ_ONLY_REF, or
  // null = yourself.
  viewAsRef: string | null;
  // Resolved entity for avatar/name; may lag a render behind viewAsRef.
  viewAsEntity: Entity | undefined;
  // The login the `?as=` PR endpoints need; undefined when viewing as yourself
  // or as the synthetic read-only user.
  viewAsLogin: string | undefined;
  // Group NAMES the viewed user is a member of (namespace-agnostic), so gated
  // surfaces can reflect the viewed user's access. Empty when not viewing.
  viewAsGroups: string[];
  // Human label for the active lens ("Read-only user" / a display name).
  viewAsLabel: string | undefined;
  // True for the synthetic read-only identity.
  isReadOnly: boolean;
  viewingAs: boolean;
  setViewAs: (ref: string | null) => void;
};

const EMPTY: ViewAsState = {
  canViewAs: false,
  viewAsRef: null,
  viewAsEntity: undefined,
  viewAsLogin: undefined,
  viewAsGroups: [],
  viewAsLabel: undefined,
  isReadOnly: false,
  viewingAs: false,
  setViewAs: () => {},
};

const ViewAsContext = createContext<ViewAsState>(EMPTY);

/** Read the app-wide View-as lens. Safe to call from any component. */
export const useViewAs = (): ViewAsState => useContext(ViewAsContext);

// Whether the signed-in user may use the lens. The BACKEND owns the policy
// (m42.viewAs.groups); this just asks the pull-requests-reader's probe. Hiding
// the control is UX -- the `?as=` gate server-side is the authority.
export const useCanViewAs = (): boolean => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const { value } = useAsync(async () => {
    const base = await discoveryApi.getBaseUrl('pull-requests-reader');
    const res = await fetchApi.fetch(`${base}/lens-access`);
    if (!res.ok) return false;
    return ((await res.json()) as { allowed?: boolean }).allowed === true;
  }, []);
  return value === true;
};

export const ViewAsProvider = ({ children }: { children: React.ReactNode }) => {
  const canViewAs = useCanViewAs();
  const storageApi = useApi(storageApiRef).forBucket(STORAGE_BUCKET);
  const catalogApi = useApi(catalogApiRef);

  // Persist the selection so the lens survives navigation and reload.
  const [storedRef, setStoredRef] = useState<string | null>(
    () => storageApi.snapshot<string>(STORAGE_KEY).value ?? null,
  );

  const setViewAs = useCallback(
    (ref: string | null) => {
      setStoredRef(ref);
      if (ref) storageApi.set(STORAGE_KEY, ref);
      else storageApi.remove(STORAGE_KEY);
    },
    [storageApi],
  );

  // Fail-closed: the lens only applies if the backend gate allows it. A revoked
  // admin keeps a stale stored ref but it's ignored until they're allowed.
  const viewAsRef = canViewAs ? storedRef : null;
  const isReadOnly = viewAsRef === READ_ONLY_REF;

  // No catalog lookup for the synthetic read-only identity.
  const { value: viewAsEntity } = useAsync(
    async () =>
      viewAsRef && !isReadOnly
        ? await catalogApi.getEntityByRef(viewAsRef).catch(() => undefined)
        : undefined,
    [viewAsRef, isReadOnly],
  );

  // Read-only has no GitHub login (never scoped via ?as=).
  const viewAsLogin = isReadOnly ? undefined : loginFromRef(viewAsRef);

  // The viewed user's group memberships (names), from the resolved entity's
  // memberOf relations. Empty for read-only (no memberships).
  const viewAsGroups = useMemo(() => {
    if (!viewAsRef || isReadOnly || !viewAsEntity) return [];
    return (viewAsEntity.relations ?? [])
      .filter(r => r.type === 'memberOf' && r.targetRef.startsWith('group:'))
      .map(r => r.targetRef.split('/').pop() ?? '')
      .filter(Boolean);
  }, [viewAsRef, isReadOnly, viewAsEntity]);

  let viewAsLabel: string | undefined;
  if (!viewAsRef) viewAsLabel = undefined;
  else if (isReadOnly) viewAsLabel = 'Read-only user';
  else viewAsLabel = displayNameOf(viewAsEntity) ?? viewAsLogin ?? 'another user';

  const state: ViewAsState = {
    canViewAs,
    viewAsRef,
    viewAsEntity: viewAsRef && !isReadOnly ? viewAsEntity : undefined,
    viewAsLogin,
    viewAsGroups,
    viewAsLabel,
    isReadOnly,
    viewingAs: viewAsRef !== null,
    setViewAs,
  };

  return (
    <ViewAsContext.Provider value={state}>{children}</ViewAsContext.Provider>
  );
};

// The entityRef's name IS the GitHub login the PR endpoints key `?as=` on.
function loginFromRef(viewAsRef: string | null): string | undefined {
  if (!viewAsRef) return undefined;
  try {
    return parseEntityRef(viewAsRef).name;
  } catch {
    return undefined;
  }
}
